import type { DateValue } from "@internationalized/date";
import { getLocalTimeZone, parseDate } from "@internationalized/date";
import type { RangeValue } from "@react-types/shared";
import type { Metric } from "@unbox/api";
import { BarChartSquare01, Calendar as CalendarIcon } from "@untitledui/icons";
import { memo, type PropsWithChildren, useCallback, useMemo } from "react";
import { useDateFormatter } from "react-aria";
import { DateRangeCalendarPopover } from "@/components/application/date-picker/date-range-calendar-popover";
import { Button } from "@/components/base/buttons/button";
import { Label } from "@/components/base/input/label";
import { HorizontalTabs } from "@/components/base/tabs/horizontal-tabs";
import { SearchableMultiSelect } from "@/components/filters/searchable-multi-select";
import { FilterPanelContainer, FilterPanelHeader, FilterSection } from "@/components/filters/shared";
import { Accordion } from "@/components/ui/accordion";
import { Form, FormField } from "@/components/ui/form";
import { AVAILABLE_METRICS } from "@/constants/metrics";
import { type ReportFiltersState, useReportFilters } from "@/hooks/use-report-filters";
import { cx } from "@/utils/cx";

type ReportFilterPanelProps = {
  open: boolean;
  close: () => void;
  filters: ReportFiltersState;
  onFiltersChange: (filters: ReportFiltersState) => void;
} & PropsWithChildren;

// Memoized Date Range Field component
type DateRangeFieldProps = {
  label: string;
  value: { start: string; end: string } | null;
  onChange: (value: { start: string; end: string } | null) => void;
};

const DateRangeField = memo(({ label, value, onChange }: DateRangeFieldProps) => {
  const formatter = useDateFormatter({
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const parsedValue = useMemo(() => {
    if (!value) return null;
    return {
      start: parseDate(value.start),
      end: parseDate(value.end),
    };
  }, [value]);

  const formattedRange = useMemo(() => {
    if (!parsedValue) return "Select dates";
    const startStr = formatter.format(parsedValue.start.toDate(getLocalTimeZone()));
    const endStr = formatter.format(parsedValue.end.toDate(getLocalTimeZone()));
    return `${startStr} – ${endStr}`;
  }, [parsedValue, formatter]);

  const isActive = !!value;

  const handleChange = useCallback(
    (range: RangeValue<DateValue> | null) => {
      if (range) {
        onChange({
          start: range.start.toString(),
          end: range.end.toString(),
        });
      } else {
        onChange(null);
      }
    },
    [onChange],
  );

  const handleReset = useCallback(() => {
    onChange(null);
  }, [onChange]);

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-sm text-tertiary">{label}</Label>
      <DateRangeCalendarPopover
        value={parsedValue}
        onChange={handleChange}
        onReset={handleReset}
        resetText="Clear"
        placement="bottom left"
      >
        <Button
          size="sm"
          color={isActive ? "tertiary" : "secondary"}
          iconLeading={<CalendarIcon className={cx("size-5 text-placeholder", isActive && "text-brand-tertiary")} />}
          className={cx(
            "w-full justify-start text-sm font-medium",
            isActive && "bg-brand-primary text-brand-primary ring-1 hover:bg-brand-secondary ring-inset ring-brand",
          )}
        >
          {formattedRange}
        </Button>
      </DateRangeCalendarPopover>
    </div>
  );
});

DateRangeField.displayName = "DateRangeField";

export const ReportFilterPanel = ({ open, close, filters, onFiltersChange }: ReportFilterPanelProps) => {
  const { form, reset, metricsSearch, setMetricsSearch, filteredMetricsOptions } = useReportFilters(
    filters,
    onFiltersChange,
  );

  const handleClear = () => {
    reset();
  };

  return (
    <FilterPanelContainer open={open}>
      <FilterPanelHeader onClear={handleClear} onClose={close} />
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        <Form {...form}>
          <div className="flex flex-col gap-6">
            {/* Layout Toggle - Always Visible at Top Level */}
            <FormField
              control={form.control}
              name="layout"
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <Label className="text-sm text-tertiary">Layout</Label>
                  <HorizontalTabs
                    tabs={[
                      { id: "all", label: "All" },
                      { id: "chart", label: "Chart" },
                      { id: "table", label: "Table" },
                    ]}
                    activeTab={field.value}
                    onTabChange={(tabId) => field.onChange(tabId)}
                  />
                </div>
              )}
            />

            {/* Expandable Sections */}
            <Accordion type="single" collapsible className="w-full">
              {/* Metrics Section */}
              <FilterSection id="metrics" icon={<BarChartSquare01 className="w-5 h-5" />} title="Metrics">
                <FormField
                  control={form.control}
                  name="metrics"
                  render={({ field }) => (
                    <SearchableMultiSelect
                      field={{
                        ...field,
                        value: field.value.map((m: Metric) => ({
                          id: m,
                          label: AVAILABLE_METRICS.find((am) => am.value === m)?.label ?? m,
                        })),
                        onChange: (newValue: Array<{ id: string; label: string }>) => {
                          field.onChange(newValue.map((v) => v.id as Metric));
                        },
                      }}
                      label="Metrics Included"
                      search={metricsSearch}
                      setSearch={setMetricsSearch}
                      options={filteredMetricsOptions}
                      placeholder="Search metrics..."
                      emptyMessage="No metrics found"
                    />
                  )}
                />
              </FilterSection>

              {/* Report Attributes Section */}
              <FilterSection
                id="report-attributes"
                icon={<CalendarIcon className="w-5 h-5" />}
                title="Report Attributes"
              >
                {/* Created Date Range */}
                <FormField
                  control={form.control}
                  name="createdDateRange"
                  render={({ field }) => (
                    <DateRangeField label="Created" value={field.value} onChange={field.onChange} />
                  )}
                />

                {/* Last Edited Date Range */}
                <FormField
                  control={form.control}
                  name="editedDateRange"
                  render={({ field }) => (
                    <DateRangeField label="Last Edited" value={field.value} onChange={field.onChange} />
                  )}
                />
              </FilterSection>
            </Accordion>
          </div>
        </Form>
      </div>
    </FilterPanelContainer>
  );
};
